import { createContext, use, useId, useState, type ReactNode } from "react";
import { Link } from "react-router";
import type { PolicyAcceptance } from "@patent-studio/api-types";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAcceptPolicies, useConsentStatus } from "@/hooks/use-consent";
import { findPolicy, type Policy } from "@/policies";

interface PendingPolicy {
  version: number;
  policy: Policy;
}

/** The app's own loading shape, so the gate does not introduce a second one. */
function FullScreenMessage({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      {children}
    </div>
  );
}

/**
 * One policy: the statement being agreed to, and a way to go and read it.
 *
 * The link sits outside the `<label>` rather than inside the acknowledgement
 * text. A link nested in a label is still part of the label's click target, so
 * going off to read the document would tick the box agreeing to it on the way
 * past — the one interaction this dialog must not get wrong.
 */
function PolicyAcknowledgement({
  policy,
  fieldId,
  checked,
  onCheckedChange,
}: {
  policy: Policy;
  fieldId: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-start gap-3">
        <Checkbox
          id={fieldId}
          className="mt-0.5"
          checked={checked}
          onCheckedChange={(state) => {
            onCheckedChange(state === true);
          }}
        />
        <label htmlFor={fieldId} className="text-sm leading-snug">
          {policy.document.acknowledgement}
        </label>
      </div>
      <p className="pl-7 text-xs">
        <Link
          to={`/policies/${policy.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2"
        >
          Read the {policy.document.title}
        </Link>
        <span className="text-muted-foreground"> (opens in a new tab)</span>
      </p>
    </div>
  );
}

function ConsentDialog({
  pending,
  isSaving,
  saveFailed,
  onAccept,
}: {
  pending: PendingPolicy[];
  isSaving: boolean;
  saveFailed: boolean;
  onAccept: (acceptances: PolicyAcceptance[]) => void;
}) {
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set());
  const [unticked, setUnticked] = useState(false);
  const fieldPrefixId = useId();

  const boxId = (policyId: string) => `${fieldPrefixId}-${policyId}`;

  const handleAccept = () => {
    const missing = pending.find(({ policy }) => !ticked.has(policy.id));
    if (missing) {
      setUnticked(true);
      // Focus rather than disabling the button: a disabled control leaves the
      // tab order and announces nothing, so a screen reader user would be told
      // only that pressing it did nothing.
      document.getElementById(boxId(missing.policy.id))?.focus();
      return;
    }

    setUnticked(false);
    onAccept(
      pending.map(({ policy, version }) => ({ policy_id: policy.id, version })),
    );
  };

  const problem = unticked
    ? "Please confirm each statement to continue."
    : saveFailed
      ? "We could not record your acceptance. Please try again."
      : null;

  return (
    <AlertDialog open>
      <AlertDialogContent
        className="max-w-lg"
        // Escape is the only dismissal an AlertDialog offers: unlike a plain
        // Dialog it already refuses outside clicks, which is why there is no
        // `onInteractOutside` here — the prop does not exist on this primitive.
        // Every other AlertDialog in the app keeps its Escape; this one is the
        // gate, so it must not have one.
        onEscapeKeyDown={(event: Event) => {
          event.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Before you start</AlertDialogTitle>
          <AlertDialogDescription>
            Please read and accept the following before using Patent Studio.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          {pending.map(({ policy }) => (
            <PolicyAcknowledgement
              key={policy.id}
              policy={policy}
              fieldId={boxId(policy.id)}
              checked={ticked.has(policy.id)}
              onCheckedChange={(isChecked) => {
                setTicked((previous) => {
                  const next = new Set(previous);
                  if (isChecked) next.add(policy.id);
                  else next.delete(policy.id);
                  return next;
                });
              }}
            />
          ))}
        </div>

        {problem !== null && (
          <p role="alert" className="text-sm text-destructive">
            {problem}
          </p>
        )}

        <div className="flex justify-end">
          <Button onClick={handleAccept} disabled={isSaving}>
            {isSaving ? "Saving…" : "Accept and continue"}
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * True only in a tree the gate has let through, where someone is signed in and
 * has accepted every policy. The public policy pages wear the same header as the
 * signed-in screens, so a control that must not act before consent asks this
 * rather than assuming its header puts it behind the gate.
 */
const PastTheGateContext = createContext(false);

export function usePastConsentGate(): boolean {
  return use(PastTheGateContext);
}

/**
 * Stands between a signed-in user and the app until they have accepted every
 * policy that is outstanding for them.
 *
 * Mounted inside `ProtectedRoute`, the only node both the v1 layout and the v2
 * shell pass through, so one instance covers both.
 *
 * The app is not rendered behind the dialog. A modal overlay would stop the
 * attorney *clicking* anything, but the pages underneath would still mount and
 * fetch, and the requirement is that the notice comes before any data changes
 * hands, not merely before the first click.
 *
 * There is no decline button. Accepting is the only action, which is what "no
 * fallback access on decline" means in practice: someone who will not accept
 * closes the tab, and nothing in the app has loaded.
 */
export function ConsentGate({ children }: { children: ReactNode }) {
  const { data, isPending, isError, refetch } = useConsentStatus();
  const accept = useAcceptPolicies();

  if (isPending) {
    return (
      <FullScreenMessage>
        <p className="text-muted-foreground">Loading...</p>
      </FullScreenMessage>
    );
  }

  // Fail closed. Not knowing whether the terms were accepted is not the same as
  // knowing they were, so a failed check blocks — but recoverably, because a
  // dead end with no button is indistinguishable from a broken app.
  if (isError) {
    return (
      <FullScreenMessage>
        <p className="text-muted-foreground">
          We could not check which legal documents you need to accept.
        </p>
        <Button onClick={() => void refetch()}>Try again</Button>
      </FullScreenMessage>
    );
  }

  if (data.outstanding.length === 0) {
    return <PastTheGateContext value>{children}</PastTheGateContext>;
  }

  const pending = data.outstanding.flatMap((item) => {
    const policy = findPolicy(item.policy_id);
    // The version has to match, not just the id. A bundle older than the server
    // still holds the previous wording, and showing that while submitting the
    // server's newer version would record agreement to text the user never saw
    // — the exact thing versioning every acceptance exists to prevent.
    return policy && policy.document.version === item.version
      ? [{ version: item.version, policy }]
      : [];
  });

  // Either a policy this bundle has never heard of, or one it knows at an older
  // version. Both mean the tab has been open across a deploy, and neither can be
  // accepted honestly from what is loaded, so ask for a reload.
  if (pending.length !== data.outstanding.length) {
    return (
      <FullScreenMessage>
        <p className="text-muted-foreground">
          There are new legal documents to review. Please reload the page to
          continue.
        </p>
        <Button
          onClick={() => {
            window.location.reload();
          }}
        >
          Reload
        </Button>
      </FullScreenMessage>
    );
  }

  return (
    <ConsentDialog
      pending={pending}
      isSaving={accept.isPending}
      saveFailed={accept.isError}
      onAccept={(acceptances) => {
        accept.mutate(acceptances);
      }}
    />
  );
}
